import type { HttpError } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { Tenant } from '@sirius/contracts/admin'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ListEmptyState } from '~/components/listEmptyState'
import { EditButton } from '~/components/refineUi/buttons/edit'
import { ShowButton } from '~/components/refineUi/buttons/show'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import {
  DataTableFilterCombobox,
  DataTableFilterDropdownText,
} from '~/components/refineUi/dataTable/dataTableFilter'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { formatDateTime } from '~/i18n/format'
import { OpenTenantButton, TenantStatusBadge } from './tenantParts'

export function TenantList() {
  const { t } = useTranslation()
  const statusOptions = useMemo(
    () => [
      { label: t('tenantsPage.common.statusValue.active'), value: 'active' },
      {
        label: t('tenantsPage.common.statusValue.suspended'),
        value: 'suspended',
      },
    ],
    [t],
  )
  const columns = useMemo<ColumnDef<Tenant>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('tenantsPage.common.name')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownText
              defaultOperator="contains"
              operators={['contains']}
              column={column}
              table={table}
              placeholder={t('tenantsPage.list.searchName')}
            />
          </div>
        ),
      },
      {
        id: 'slug',
        accessorKey: 'slug',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('tenantsPage.common.slug')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownText
              defaultOperator="contains"
              operators={['contains']}
              column={column}
              table={table}
              placeholder={t('tenantsPage.list.searchSlug')}
            />
          </div>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('tenantsPage.common.status')}
            <DataTableFilterCombobox
              column={column}
              table={table}
              options={statusOptions}
              defaultOperator="in"
              operators={['in']}
              multiple
            />
          </div>
        ),
        cell: ({ getValue }) => (
          <TenantStatusBadge status={getValue<Tenant['status']>()} />
        ),
        meta: { filterOperator: 'in' },
        enableSorting: false,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('tenantsPage.common.created')}
            <DataTableSorter column={column} />
          </div>
        ),
        cell: ({ getValue }) => formatDateTime(getValue<Date>()),
        enableColumnFilter: false,
      },
      {
        id: 'actions',
        header: '',
        // Show, Edit and Open side by side
        size: 260,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <ShowButton
              recordItemId={row.original.id}
              size="sm"
            />
            <EditButton
              recordItemId={row.original.id}
              size="sm"
            />
            <OpenTenantButton
              tenant={row.original}
              kind="open"
              size="sm"
            />
          </div>
        ),
      },
    ],
    [t, statusOptions],
  )

  const table = useTable<Tenant, HttpError>({
    columns,
    refineCoreProps: {
      resource: 'tenants',
      syncWithLocation: true,
      pagination: { pageSize: 20 },
      sorters: { initial: [{ field: 'name', order: 'asc' }] },
    },
  })
  const { tableQuery, filters } = table.refineCore
  const isEmpty = tableQuery.data?.total === 0

  return (
    <ListView>
      <ListViewHeader title={t('tenants.tenants')} />
      {isEmpty ? (
        <ListEmptyState
          resource="tenants"
          emptyText={t('tenantsPage.list.empty')}
          noMatchText={t('tenantsPage.list.noMatch')}
          hasFilters={filters.length > 0}
          // via the column filters, so react-table and Refine stay in sync
          onClearFilters={() => table.reactTable.setColumnFilters([])}
        />
      ) : (
        <DataTable table={table} />
      )}
    </ListView>
  )
}
